import { DOCUMENT, inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LocalStorageService } from '../storage/local-storage.service';
import { WindowService } from '../window/window.service';
import {
  ColorSchemePreference,
  isColorSchemePreference,
} from './color-scheme';

const darkSchemeQuery = '(prefers-color-scheme: dark)';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly windowService = inject(WindowService);

  readonly preference = signal<ColorSchemePreference>(
    this.readStoredPreference()
  );
  private readonly darkState = signal(this.resolveDark(this.preference()));
  readonly dark = this.darkState.asReadonly();
  private readonly darkSubject = new BehaviorSubject(this.darkState());
  readonly dark$ = this.darkSubject.asObservable();

  constructor() {
    this.applyDom(this.darkState());
    this.watchSystem();
  }

  setPreference(preference: ColorSchemePreference): void {
    this.preference.set(preference);
    this.localStorageService.setItem('preferred-color-scheme', preference);
    this.apply();
  }

  private readStoredPreference(): ColorSchemePreference {
    const stored = this.localStorageService.getItem('preferred-color-scheme');
    return isColorSchemePreference(stored) ? stored : 'system';
  }

  private watchSystem(): void {
    this.windowService
      .getWindow()
      .matchMedia(darkSchemeQuery)
      .addEventListener('change', () => this.apply());
  }

  private apply(): void {
    const dark = this.resolveDark(this.preference());
    this.darkState.set(dark);
    this.darkSubject.next(dark);
    this.applyDom(dark);
  }

  private applyDom(dark: boolean): void {
    const html = this.document.documentElement;
    html.classList.toggle('ion-palette-dark', dark);
    html.style.colorScheme = dark ? 'dark' : 'light';

    const meta = this.document.querySelector('meta[name="color-scheme"]');
    if (meta) {
      meta.setAttribute(
        'content',
        this.preference() === 'system' ? 'light dark' : this.preference()
      );
    }
  }

  private resolveDark(preference: ColorSchemePreference): boolean {
    return (
      preference === 'dark' || (preference === 'system' && this.systemDark())
    );
  }

  private systemDark(): boolean {
    return this.windowService.getWindow().matchMedia(darkSchemeQuery).matches;
  }
}
